import React, { useEffect, useState } from "react";

const PALETTE = ["#1B4383", "#2F6B45", "#A87F31", "#6D4AA6", "#C24E4E", "#2C8BB0", "#B0632C", "#4B6584", "#8E44AD", "#16A085"];
const fmtMoney = (cur, v) => `${cur} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

/**
 * Reusable "share of total" analytics panel: optional metric toggle + currency toggle,
 * a 100% contribution bar, and a ranked, drill-in list.
 *
 * items: [{ key, label, meta, badge?, conv?, metricValues: { <metricKey>: { <cur>: number } } }]
 * metrics: [{ key, label }]  (single metric hides the toggle)
 */
export default function ContributionPanel({ title, subtitle, Icon, currencies = ["USD"], metrics, items = [], onOpen, emptyHint, testid = "contribution-panel", colorMap, currency: currencyProp, onCurrencyChange }) {
  const mlist = metrics && metrics.length ? metrics : [{ key: "value", label: "Value" }];
  const [curState, setCurState] = useState(currencies[0]);
  const [metric, setMetric] = useState(mlist[0].key);
  const cur = currencyProp !== undefined ? currencyProp : curState;
  const setCur = onCurrencyChange || setCurState;
  useEffect(() => { if (currencyProp === undefined && !currencies.includes(curState)) setCurState(currencies[0]); }, [currencies]); // eslint-disable-line

  const ranked = items
    .map((it) => ({ ...it, val: (it.metricValues?.[metric]?.[cur]) || 0 }))
    .sort((a, b) => b.val - a.val);
  const total = ranked.reduce((s, r) => s + r.val, 0);
  const colorFor = {};
  ranked.forEach((r, i) => { colorFor[r.key] = (colorMap && colorMap[r.key]) || PALETTE[i % PALETTE.length]; });

  return (
    <div className="mb-6 rounded-xl border border-border bg-white p-5" data-testid={testid}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]">{Icon ? <Icon className="h-4 w-4" /> : null}</span>
          <div>
            <div className="font-heading font-semibold text-slate-900">{title}</div>
            {subtitle ? <div className="text-xs text-muted-foreground">{subtitle}</div> : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mlist.length > 1 && (
            <div className="flex rounded-md border border-border p-0.5">
              {mlist.map((m) => (
                <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`${testid}-metric-${m.key}`}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${metric === m.key ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          )}
          {currencies.length > 1 && (
            <div className="flex rounded-md border border-border p-0.5">
              {currencies.map((c) => (
                <button key={c} onClick={() => setCur(c)} data-testid={`${testid}-cur-${c}`}
                  className={`rounded px-2.5 py-1 text-xs font-medium ${cur === c ? "bg-brand text-white" : "text-muted-foreground hover:text-slate-900"}`}>
                  {c}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {total === 0 ? (
        <div className="rounded-lg bg-slate-50 py-8 text-center text-sm text-muted-foreground">
          {emptyHint || `No ${mlist.find((m) => m.key === metric)?.label.toLowerCase()} value yet in ${cur}.`}
        </div>
      ) : (
        <>
          <div className="flex h-5 w-full overflow-hidden rounded-full bg-slate-100" data-testid={`${testid}-bar`}>
            {ranked.filter((r) => r.val > 0).map((r) => (
              <div key={r.key} title={`${r.label}: ${fmtMoney(cur, r.val)} (${((r.val / total) * 100).toFixed(1)}%)`}
                style={{ width: `${(r.val / total) * 100}%`, backgroundColor: colorFor[r.key], transition: "width .5s ease" }}
                className="h-full first:rounded-l-full last:rounded-r-full" />
            ))}
          </div>
          <div className="mt-4 space-y-1.5 max-h-[232px] overflow-y-auto pr-1" data-testid={`${testid}-list`}>
            {ranked.map((r) => (
              <button key={r.key} onClick={() => onOpen && onOpen(r.key)} data-testid={`${testid}-row-${r.key}`}
                className="flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-50">
                <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: colorFor[r.key] }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate font-medium text-slate-800">{r.label}</span>
                    {r.badge ? <span className="shrink-0 rounded-full bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold text-[#1B4383]">{r.badge}</span> : null}
                    {r.conv ? <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">{r.conv}</span> : null}
                  </div>
                  <div className="truncate text-[11px] text-muted-foreground">{r.meta}</div>
                </div>
                <span className="shrink-0 whitespace-nowrap text-right text-xs tabular-nums text-slate-900">{r.val ? fmtMoney(cur, r.val) : "—"}</span>
                <span className="w-10 shrink-0 text-right font-semibold text-slate-900">{total ? ((r.val / total) * 100).toFixed(0) : 0}%</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
